import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';
import './TicketAppointments.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LENGTHS = [15, 30, 45, 60, 90, 120];

/**
 * How long the browser says we are from UTC, named for the person reading it.
 *
 * The server stores and the .ics carries UTC instants; everything on this
 * screen is in the reader's own zone, because that is the only zone they can
 * act on. Stating it is not decoration: a Malta contractor arranging a time
 * for somebody travelling is the case where an unlabelled "14:30" is a wasted
 * morning.
 */
const localZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time';
  } catch {
    return 'your local time';
  }
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { timeZone: 'Europe/Malta',
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });
};

const timeOnly = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('en-GB', { timeZone: 'Europe/Malta', hour: '2-digit', minute: '2-digit' });
};

/**
 * A datetime-local value for an instant, in the browser's own zone.
 *
 * Not toISOString().slice(0,16): that is UTC, and putting a UTC string into a
 * datetime-local input silently shifts every existing appointment by the
 * offset the moment somebody opens the form to change it - so pressing Save
 * without touching the time would move the meeting two hours and send
 * everybody a reschedule.
 */
const forInput = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** The next whole half hour, which is what somebody arranging a call means. */
const nextHalfHour = () => {
  const d = new Date(Date.now() + 30 * 60000);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() > 30 ? 60 : 30);
  return forInput(d);
};

/**
 * A time to look at this ticket, and the invitation that goes with it.
 *
 * Gordon, 11 October: the contractor sets an appointment to fix the issue -
 * an online session or physically coming in - invites the user, and both of
 * them save it in their own calendar off-app, by email.
 *
 * SO THE DELIVERY IS PART OF THE FEATURE, NOT A SIDE EFFECT, and this
 * component says what happened to it. An appointment whose invitation bounced
 * looks exactly like one that arrived, from in here: the row is identical.
 * The server answers every write with a `delivery` array, so a failed send is
 * shown against the appointment it belongs to, by address, with the reason -
 * because the alternative is the contractor believing the user has it in their
 * calendar when they have nothing.
 *
 * `canArrange` is the caller's decision rather than this component's, for the
 * same reason TicketExpenses works that way: the staff ticket page, the work
 * order page and the contractor's own dialog each know something different
 * about who is looking. Read-only is a real mode here - the person who raised
 * the ticket should see when somebody is coming without being able to move it.
 *
 * ONE COMPONENT, not markup on three pages. The expenses panel was built in
 * one place and Gordon could not find it three times running, because the
 * places he looked were the other three.
 */
const TicketAppointments = ({ ticketId, auth, canArrange = false, onChanged }) => {
  // Both links into a session below say where they were pressed, so the
  // session page can come back to this ticket - the staff page or the
  // contractor's, whichever this is - when the call ends.
  const here = useLocation();
  const from = `?from=${encodeURIComponent(`${here.pathname}${here.search}`)}`;
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // null = closed; 'new' = arranging one; an id = changing that one.
  const [editing, setEditing] = useState(null);
  const [kind, setKind] = useState('online');
  const [startsAt, setStartsAt] = useState(nextHalfHour);
  const [minutes, setMinutes] = useState(30);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');

  // Keyed by appointment id, so a failure stays visible against the right row
  // after the list reloads.
  const [delivery, setDelivery] = useState({});

  const zone = useMemo(localZone, []);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try {
      const res = await axios.get(`${API_BASE}/tickets/${ticketId}/appointments`, auth);
      // Array.isArray, not `res.data || []`. The endpoint answers an array,
      // and `data?.something || []` on an array is how a page went white
      // once already in this project: [].entries is a function, truthy, and
      // React calls a function handed to a setter as an updater.
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      // A read that failed is not "nothing arranged". Saying nobody is coming
      // when the request broke is the worse of the two wrong answers.
      setError(err.response?.data?.error || 'Could not load the appointments on this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId, auth]);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setEditing(null);
    setKind('online');
    setStartsAt(nextHalfHour());
    setMinutes(30);
    setLocation('');
    setNotes('');
  };

  const openFor = (row) => {
    setEditing(row.id);
    setKind(row.kind);
    setStartsAt(forInput(row.starts_at));
    setMinutes(Math.max(1,
      Math.round((new Date(row.ends_at) - new Date(row.starts_at)) / 60000)));
    setLocation(row.location || '');
    setNotes(row.notes || '');
    setError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!startsAt) { setError('When does it start?'); return; }
    if (kind === 'physical' && !location.trim()) {
      setError('Where is it? Give an address or a room.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // The input is local wall-clock time with no zone on it; new Date()
      // reads it in the browser's zone, which is what the person meant, and
      // toISOString hands the server an unambiguous instant.
      const starts = new Date(startsAt);
      const body = {
        kind,
        starts_at: starts.toISOString(),
        minutes: Number(minutes),
        location: kind === 'physical' ? location.trim() : '',
        notes: notes.trim()
      };

      const res = editing && editing !== 'new'
        ? await axios.patch(`${API_BASE}/appointments/${editing}`, body, auth)
        : await axios.post(`${API_BASE}/tickets/${ticketId}/appointments`, body, auth);

      const saved = res.data || {};
      if (saved.id) {
        setDelivery((d) => ({ ...d, [saved.id]: saved.delivery || [] }));
      }
      reset();
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that appointment');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (row) => {
    const reason = await confirm({
      title: 'Cancel this appointment?',
      message: 'Why is it cancelled? Whoever was invited will be told.',
      prompt: { label: 'Reason', placeholder: 'Optional', required: false },
      confirmLabel: 'Cancel appointment', cancelLabel: 'Keep it', danger: true
    });
    // Cancelled with no reason is allowed - an empty string is a reason not
    // given, and blocking on it would leave stale appointments standing.
    // false means they backed out of the dialog.
    if (reason === false) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.delete(`${API_BASE}/appointments/${row.id}`, {
        ...auth,
        data: { reason: reason.trim() }
      });
      setDelivery((d) => ({ ...d, [row.id]: res.data?.delivery || [] }));
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel that appointment');
    } finally {
      setBusy(false);
    }
  };

  const upcoming = rows.filter((r) => r.state === 'scheduled'
    && new Date(r.ends_at) >= new Date());
  const rest = rows.filter((r) => !upcoming.includes(r));

  const row = (a) => {
    const failed = (delivery[a.id] || []).filter((d) => d.ok === false);
    const went = (delivery[a.id] || []).filter((d) => d.ok === true);
    const past = new Date(a.ends_at) < new Date();

    return (
      <li key={a.id} className="ap-row" data-state={a.state} data-past={past || undefined}>
        <div className="ap-when">
          <span className="ap-date">{fmt(a.starts_at)}</span>
          <span className="ap-dash">–</span>
          <span className="ap-date">{timeOnly(a.ends_at)}</span>
          <span className={`ap-kind ap-kind-${a.kind}`}>
            {a.kind === 'online' ? 'Online' : 'In person'}
          </span>
          {a.state === 'cancelled' && <span className="ap-cancelled">Cancelled</span>}
        </div>

        {/* AN IN-APP LINK, not the absolute one the server built.

            The server's join_url is for the EMAIL: it has to be absolute, and
            it is built from FRONTEND_URL so that a link sitting in somebody's
            calendar for three weeks still points at the right host. Following
            it from inside Jarvis would be a full page load - and, in any
            environment whose FRONTEND_URL is not where the reader actually is,
            a trip to the wrong host. The path below is derived from the
            appointment's own id, so nothing is invented; only the host is
            dropped. */}
        {a.kind === 'online' && a.state === 'scheduled' && (
          <Link className="ap-join" to={`/sessions/a/${a.id}${from}`}>
            Join the session
          </Link>
        )}
        {a.kind === 'physical' && a.location && (
          <div className="ap-where">{a.location}</div>
        )}
        {a.notes && <div className="ap-notes">{a.notes}</div>}
        {a.state === 'cancelled' && a.cancel_reason && (
          <div className="ap-notes ap-reason">{a.cancel_reason}</div>
        )}

        <div className="ap-by">
          Set by {a.created_by || 'somebody'}
          {a.ics_sequence > 0 && (
            <span className="ap-changed"> · changed {a.ics_sequence} time
              {a.ics_sequence === 1 ? '' : 's'}</span>
          )}
        </div>

        {/* The half of this feature that is invisible otherwise. An
            appointment whose invitation bounced looks identical to one that
            arrived. */}
        {failed.length > 0 && (
          <div className="ap-undelivered" role="alert">
            <strong>The invitation did not reach everybody.</strong>
            <ul>
              {failed.map((d) => (
                <li key={d.recipient}>{d.recipient} — {d.reason || 'unknown reason'}</li>
              ))}
            </ul>
            {went.length > 0 && (
              <div className="ap-delivered-some">
                It did reach {went.map((d) => d.recipient).join(', ')}.
              </div>
            )}
          </div>
        )}
        {failed.length === 0 && went.length > 0 && (
          <div className="ap-delivered">
            Invitation emailed to {went.map((d) => d.recipient).join(', ')}
          </div>
        )}

        {canArrange && a.state === 'scheduled' && (
          <div className="ap-actions">
            <button type="button" className="ap-link" disabled={busy}
              onClick={() => openFor(a)}>Change the time</button>
            <button type="button" className="ap-link ap-link-danger" disabled={busy}
              onClick={() => cancel(a)}>Cancel it</button>
          </div>
        )}
      </li>
    );
  };

  return (
    <section className="ap-panel">
      <header className="ap-head">
        <h4 className="ap-title">Appointments</h4>
        {canArrange && editing === null && (
          <div className="ap-head-actions">
            {/* Not everything needs arranging. "Can you look now?" is the
                commonest version of this conversation, and it should not
                require inventing a time and emailing an invitation for it. */}
            <Link className="ap-now" to={`/tickets/${ticketId}/session${from}`}>
              Start one now
            </Link>
            <button type="button" className="ap-new"
              onClick={() => { reset(); setEditing('new'); }}>
              Arrange a time
            </button>
          </div>
        )}
      </header>

      {error && <div className="ap-error" role="alert">{error}</div>}

      {editing !== null && (
        <form className="ap-form" onSubmit={submit}>
          <fieldset className="ap-kinds">
            <legend className="ap-legend">How</legend>
            <label className="ap-radio">
              <input type="radio" name={`ap-kind-${ticketId}`} value="online"
                checked={kind === 'online'} onChange={() => setKind('online')} />
              <span>
                <strong>Online session</strong>
                <small>Screen sharing, voice and chat in the browser</small>
              </span>
            </label>
            <label className="ap-radio">
              <input type="radio" name={`ap-kind-${ticketId}`} value="physical"
                checked={kind === 'physical'} onChange={() => setKind('physical')} />
              <span>
                <strong>In person</strong>
                <small>Somebody comes to you</small>
              </span>
            </label>
          </fieldset>

          <div className="ap-fields">
            <label className="ap-field">
              <span className="ap-label">Starts</span>
              <input id={`ap-start-${ticketId}`} type="datetime-local" value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)} required />
              {/* Which zone the box above is in. Everything the server stores
                  is UTC; this is the reader's own clock. */}
              <small className="ap-hint">{zone}</small>
            </label>

            <label className="ap-field">
              <span className="ap-label">For</span>
              <select id={`ap-length-${ticketId}`} value={minutes}
                onChange={(e) => setMinutes(e.target.value)}>
                {LENGTHS.map((m) => (
                  <option key={m} value={m}>
                    {m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {kind === 'physical' && (
            <label className="ap-field ap-wide">
              <span className="ap-label">Where</span>
              <input id={`ap-where-${ticketId}`} type="text" value={location}
                placeholder="Address, floor or room"
                onChange={(e) => setLocation(e.target.value)} />
            </label>
          )}

          <label className="ap-field ap-wide">
            <span className="ap-label">Anything they should do first</span>
            <textarea id={`ap-notes-${ticketId}`} rows={2} value={notes}
              placeholder="Optional — e.g. have the error on screen before we start"
              onChange={(e) => setNotes(e.target.value)} />
          </label>

          <div className="ap-form-actions">
            <button type="submit" className="ap-save" disabled={busy}>
              {busy
                ? 'Sending the invitation…'
                : editing === 'new' ? 'Arrange it and invite' : 'Move it and tell them'}
            </button>
            <button type="button" className="ap-link" onClick={reset} disabled={busy}>
              Never mind
            </button>
          </div>
          <p className="ap-foot">
            Both of you get an email with a calendar invitation attached.
            {editing !== 'new'
              && ' Accepting it replaces the one already in their calendar.'}
          </p>
        </form>
      )}

      {loading && <p className="ap-empty">Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="ap-empty">
          {canArrange
            ? 'Nothing arranged yet.'
            : 'Nothing has been arranged on this ticket.'}
        </p>
      )}

      {upcoming.length > 0 && <ul className="ap-list">{upcoming.map(row)}</ul>}
      {rest.length > 0 && (
        <details className="ap-past">
          <summary>{rest.length} past or cancelled</summary>
          <ul className="ap-list">{rest.map(row)}</ul>
        </details>
      )}
      {confirmDialog}
    </section>
  );
};

export default TicketAppointments;
